
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  type CarouselApi 
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { EventImageWithCaption } from "@/utils/eventImageUtils";
import { EventImage } from "@/components/create-event/form/image-upload/imageUploadUtils";
import { useState, useEffect } from "react";
import { CarouselDots } from "@/components/ui/carousel-dots";

interface EventImageCarouselProps {
  images?: (EventImage | EventImageWithCaption)[] | null;
  fallbackImage?: string | null;
  eventTitle: string;
  className?: string;
  aspectRatio?: number;
}

export const EventImageCarousel = ({ 
  images, 
  fallbackImage, 
  eventTitle,
  className,
  aspectRatio = 1
}: EventImageCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      console.log('Selected slide:', api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    onSelect();

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const scrollTo = (index: number) => {
    console.log('Scrolling to slide:', index);
    api?.scrollTo(index);
  };
  
  const hasImages = images && images.length > 0;
  const imageUrls = hasImages 
    ? images!.sort((a, b) => a.display_order - b.display_order)
    : fallbackImage 
      ? [{ image_id: 'fallback', image_url: fallbackImage, is_primary: true, display_order: 0 }] 
      : [];

  if (imageUrls.length === 0) {
    return (
      <div className={cn("w-full rounded-2xl overflow-hidden px-4", className)}>
        <AspectRatio ratio={aspectRatio}>
          <div
            className="w-full h-full rounded-2xl"
            style={{
              background: 'linear-gradient(90deg, hsla(277, 75%, 84%, 1) 0%, hsla(297, 50%, 51%, 1) 100%)'
            }}
            aria-label={`Placeholder for ${eventTitle}`}
          />
        </AspectRatio>
      </div>
    );
  }

  if (imageUrls.length === 1) {
    const image = imageUrls[0];
    const caption = 'caption' in image && !image.is_guest_avatar ? image.caption : undefined;
    
    return (
      <div className={cn("w-full px-4", className)}>
        <AspectRatio ratio={aspectRatio}>
          <div className="relative w-full h-full">
            <div 
              className="w-full h-full rounded-2xl overflow-hidden"
              style={{
                backgroundImage: `url(${image.image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              aria-label={`Image for ${eventTitle}`}
            />
            {caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm text-center">
                {caption}
              </div>
            )}
          </div>
        </AspectRatio>
      </div>
    );
  }

  return (
    <div className={cn("w-full px-4", className)}>
      <div className="relative">
        <Carousel 
          setApi={setApi}
          opts={{
            loop: true,
            align: "center",
            duration: 30,
          }}
          className="w-full"
        >
          <CarouselContent>
            {imageUrls.map((image, index) => (
              <CarouselItem key={`${image.image_id}-${index}`}>
                <AspectRatio ratio={aspectRatio}>
                  <div 
                    className="w-full h-full rounded-2xl overflow-hidden"
                    style={{
                      backgroundImage: `url(${image.image_url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                    aria-label={`Image for ${eventTitle}`}
                  />
                </AspectRatio>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        
        {imageUrls.length > 1 && (
          <CarouselDots 
            total={count} 
            activeIndex={current} 
            onDotClick={scrollTo}
            className="mt-3" 
          />
        )}
      </div>
    </div>
  );
};

